import type { SmsIntegration, SmsProviderSummary } from '@chukua/common';
import { Alert, Button, Form, Input, Popconfirm, type FormInstance } from 'antd';
import { KenyanMobileInput } from '../../../../../shared/components/KenyanMobileInput';
import { kenyanMobileRules } from '../../../../../shared/policies/kenyan-mobile-rules';
import { ConfigFields } from '../../../components/ConfigFields';
import { IntegrationLogo } from '../../../components/IntegrationLogo';
import type { SmsFormValues, useSmsIntegrationSheet } from '../hooks/use-sms-integration-sheet';
import { changesCredentials, providerFormInitialValues } from '../../../policies/messaging-provider.policy';

function submitLabel(isSettings: boolean, sendingName: string | undefined): string {
  if (isSettings) return 'Save';
  return sendingName ? 'Send test, install and switch' : 'Send test and install';
}

interface SmsIntegrationFormProps {
  sheet: ReturnType<typeof useSmsIntegrationSheet>;
  provider: SmsProviderSummary;
  /** The provider sending now, if any: installing another replaces it, and the form says so. */
  sendingName: string | undefined;
  /** Settings only: turning the provider off, or uninstalling one that is off. */
  onStopSending: (integration: SmsIntegration) => Promise<void>;
  onUninstall: (integration: SmsIntegration) => Promise<void>;
}

/** The provider's fields, rendered from its config schema, and the number its test text goes to. */
export function SmsIntegrationForm({ sheet, provider, sendingName, onStopSending, onUninstall }: SmsIntegrationFormProps) {
  const [form] = Form.useForm<SmsFormValues>();
  const { integration } = sheet;

  return (
    <Form<SmsFormValues>
      form={form}
      layout="vertical"
      requiredMark={false}
      disabled={sheet.submitting}
      onFinish={sheet.save}
      initialValues={providerFormInitialValues(provider, integration)}
    >
      <div className="mb-5 flex items-center gap-3">
        <IntegrationLogo logoSvg={provider.logoSvg} name={provider.displayName} />
        <span className="text-base font-semibold text-fg">{provider.displayName}</span>
      </div>
      {sheet.error && <Alert className="mb-5" type="error" showIcon title={sheet.error} />}
      <Form.Item name="displayName" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
        <Input />
      </Form.Item>
      <ConfigFields fields={provider.fields} secretsSet={integration?.secretsSet ?? []} />
      <TestTextField form={form} sheet={sheet} provider={provider} />
      {!integration && sendingName && (
        <Alert
          className="mb-5"
          type="info"
          showIcon
          title={`${provider.displayName} will replace ${sendingName}`}
          description={`Texts go out through ${provider.displayName} once it is installed. ${sendingName} stays installed but off, so you can switch back.`}
        />
      )}
      <Form.Item className="mb-0 pt-2">
        <Button type="primary" htmlType="submit" loading={sheet.submitting}>
          {submitLabel(Boolean(integration), sendingName)}
        </Button>
      </Form.Item>
      {integration && <DangerZone integration={integration} onStopSending={onStopSending} onUninstall={onUninstall} />}
    </Form>
  );
}

interface TestTextFieldProps {
  form: FormInstance<SmsFormValues>;
  sheet: SmsIntegrationFormProps['sheet'];
  provider: SmsProviderSummary;
}

/** Required to install, and to save changed credentials: neither provider can check them without sending a real text. */
function TestTextField({ form, sheet, provider }: TestTextFieldProps) {
  const { integration } = sheet;
  const isNeeded = () => !integration || changesCredentials(provider.fields, integration, form.getFieldsValue());
  return (
    <div className="mb-6 flex flex-col gap-3 border-t border-line pt-5">
      <div>
        <h3 className="text-sm font-semibold text-fg">Test text</h3>
        <p className="text-sm text-muted">
          {integration
            ? 'Changed credentials are tested with one real text before they are saved.'
            : `Installing sends one real text, so use your own number. It costs one text on your ${provider.displayName} account.`}
        </p>
      </div>
      <div className="flex items-start gap-2">
        <Form.Item
          className="mb-0 flex-1"
          name="testTo"
          label="Send a test text to"
          rules={[
            ...kenyanMobileRules({ required: false }),
            { validator: (_, value) => (value || !isNeeded() ? Promise.resolve() : Promise.reject(new Error('Enter a number to send the test text to'))) },
          ]}
        >
          <KenyanMobileInput />
        </Form.Item>
        {integration && (
          <Button
            className="mt-[30px]"
            loading={sheet.testing}
            onClick={async () => {
              const { testTo } = await form.validateFields(['testTo']);
              if (testTo) await sheet.sendTest(testTo);
            }}
          >
            Send test
          </Button>
        )}
      </div>
    </div>
  );
}

function DangerZone({
  integration,
  onStopSending,
  onUninstall,
}: { integration: SmsIntegration } & Pick<SmsIntegrationFormProps, 'onStopSending' | 'onUninstall'>) {
  return (
    <div className="mt-8 flex flex-col gap-3 border-t border-line pt-5">
      <h3 className="text-sm font-semibold text-fg">{integration.isSending ? 'Turn off' : 'Uninstall'}</h3>
      <p className="text-sm text-muted">
        {integration.isSending
          ? "Drivers can't sign in and app invites can't be sent until another provider sends."
          : 'Removes these credentials. Texts already sent are unaffected.'}
      </p>
      <div>
        {integration.isSending ? (
          <Popconfirm title={`Turn off ${integration.displayName}?`} okText="Turn off" onConfirm={() => onStopSending(integration)}>
            <Button danger>Turn off</Button>
          </Popconfirm>
        ) : (
          <Popconfirm
            title={`Uninstall ${integration.displayName}?`}
            okText="Uninstall"
            okButtonProps={{ danger: true }}
            onConfirm={() => onUninstall(integration)}
          >
            <Button danger>Uninstall</Button>
          </Popconfirm>
        )}
      </div>
    </div>
  );
}
